import { useId, type MouseEvent } from 'react';
import { getImageProps } from 'next/image';
import { CheckIcon } from '@phosphor-icons/react';

import LinkButton from '_components/UI/LinkButton';
import { Breakpoints } from '@hooks/useBreakpoint';
import { useLocale } from '@hooks/useLocale';
import { localizeHref } from '@utils/localizeHref';
import { isModifiedClick } from '_utils/GA4/clickTracking';
import {
  trackWhyChooseCtaClick,
  type WhyChoosePageType,
} from '_utils/GA4/trackWhyChooseEvents';
import type {
  ResolvedWhyChoose,
  WhyChooseImage,
} from '@adapters/prismic/types/whyChoose';
import type { HitProps } from '@components/algolia/Hit';
import StaffQuote from './StaffQuote';
import QuickAddChip from './QuickAddChip';

import styles from './WhyChoose.module.scss';

export interface WhyChooseProps {
  config: ResolvedWhyChoose;
  /** First quick-addable product from `config.quickAddProductCodes`,
      resolved server-side (ED-357). */
  quickAddProduct?: HitProps | null;
  /**
   * The page the section renders on, for GA4 (ED-590): `type` on the click
   * events, and the quick add's `item_list_name` / `atc_source`. Required,
   * so every host decides how it reports.
   */
  pageType: WhyChoosePageType;
}

const IMAGE_SIZES = `(min-width: ${Breakpoints.tabletXl}px) 50vw, 100vw`;

// Art direction through one `<picture>` (same as `PromoTileCard`) so the
// browser downloads only the crop whose `<source media>` matches.
const WhyChoosePicture = ({ image }: { image: WhyChooseImage }) => {
  const {
    props: { srcSet: desktopSrcSet, sizes: desktopSizes },
  } = getImageProps({
    src: image.url,
    alt: image.alt,
    fill: true,
    sizes: IMAGE_SIZES,
  });
  const { props: mobileImageProps } = getImageProps({
    src: image.mobile?.url ?? image.url,
    alt: image.alt,
    fill: true,
    sizes: IMAGE_SIZES,
    className: styles.image,
  });

  return (
    <picture>
      <source
        media={`(min-width: ${Breakpoints.tabletXl}px)`}
        srcSet={desktopSrcSet}
        sizes={desktopSizes}
      />
      <img {...mobileImageProps} />
    </picture>
  );
};

/**
 * "Why choose [brand]?" PDP section (ED-340, Figma 891:182137): brand
 * benefits, a shop link and a staff quote over a brand image. Content and
 * targeting come from the `why_choose` Prismic type.
 */
const WhyChoose = ({
  config,
  quickAddProduct = null,
  pageType,
}: WhyChooseProps) => {
  const headingId = useId();
  const locale = useLocale();

  // The heading is the section's accessible name, so nothing renders without it.
  if (!config.heading) return null;

  const hasQuote = config.quote.length > 0;
  const linkHref = localizeHref(config.linkHref, locale);

  const ctaClickData = () => ({
    interactionLocation: `${window.location.pathname}${window.location.search}`,
    linkUrl: linkHref,
    linkText: config.linkLabel,
    coOpBrand: config.coOpBrand || null,
    pageType,
  });

  // ED-422: `click_why_choose_cta`, deferring navigation until GTM has the
  // hit (same as the hotspot CTA) unless the click opens a new tab.
  const handleLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (isModifiedClick(event)) {
      trackWhyChooseCtaClick({ data: ctaClickData() });
      return;
    }

    event.preventDefault();
    trackWhyChooseCtaClick({
      data: ctaClickData(),
      eventCallback: () => {
        window.location.href = linkHref;
      },
    });
  };

  // A middle click fires `auxclick`, not `click`. Track it and leave the
  // browser's new-tab navigation alone. Other buttons (e.g. right click
  // for the context menu) aren't a click on the link.
  const handleLinkAuxClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.button !== 1) return;
    trackWhyChooseCtaClick({ data: ctaClickData() });
  };

  return (
    <section className={styles.root} aria-labelledby={headingId}>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.contentInner}>
            <h2 id={headingId} className={styles.heading}>
              {config.heading}
            </h2>

            {config.benefits.length > 0 && (
              <ul className={styles.benefits}>
                {config.benefits.map((benefit) => (
                  <li key={benefit} className={styles.benefit}>
                    <span className={styles.checkIcon} aria-hidden="true">
                      <CheckIcon size={15} weight="bold" />
                    </span>
                    {benefit}
                  </li>
                ))}
              </ul>
            )}

            {config.linkLabel && config.linkHref && (
              <div>
                <LinkButton
                  variant="white"
                  href={linkHref}
                  onClick={handleLinkClick}
                  onAuxClick={handleLinkAuxClick}
                >
                  {config.linkLabel}
                </LinkButton>
              </div>
            )}
          </div>
        </div>

        {(config.image || hasQuote || quickAddProduct) && (
          <div className={styles.media}>
            {config.image && <WhyChoosePicture image={config.image} />}
            <span className={styles.overlay} aria-hidden="true" />
            {quickAddProduct && (
              <QuickAddChip
                product={quickAddProduct}
                coOpBrand={config.coOpBrand}
                pageType={pageType}
              />
            )}
            {hasQuote && (
              <StaffQuote
                quote={config.quote}
                attribution={config.quoteAttribution}
              />
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default WhyChoose;
